import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, Search, Copy, MapPin, Briefcase, Heart, Target } from 'lucide-react';
import axios from 'axios';

interface Audience {
  id: number;
  client_id: number;
  name: string;
  description: string | null;
  age_min: number | null;
  age_max: number | null;
  gender: string;
  locations: string | null;
  languages: string | null;
  job_titles: string | null;
  job_functions: string | null;
  seniorities: string | null;
  industries: string | null;
  company_sizes: string | null;
  interests: string | null;
  behaviors: string | null;
  custom_audiences: string | null;
  lookalike_audiences: string | null;
  platform: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AudiencesTabProps {
  clientId: number;
  onOpenAudienceModal: (audience?: Audience) => void;
}

export default function AudiencesTab({ clientId, onOpenAudienceModal }: AudiencesTabProps) {
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

  useEffect(() => {
    loadAudiences();
  }, [clientId]);

  const loadAudiences = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/audiences?client_id=${clientId}`);
      setAudiences(response.data.audiences || []);
    } catch (error) {
      console.error('Failed to load audiences:', error);
      setAudiences([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (audienceId: number) => {
    try {
      await axios.delete(`${API_BASE_URL}/audiences/${audienceId}`);
      setAudiences(audiences.filter(a => a.id !== audienceId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete audience:', error);
      alert('Failed to delete audience');
    }
  };

  const handleDuplicate = async (audience: Audience) => {
    const duplicated = {
      ...audience,
      name: `${audience.name} (Copy)`,
      id: undefined
    };
    onOpenAudienceModal(duplicated as any);
  };

  const getPlatformColor = (platform: string): string => {
    const colors: Record<string, string> = {
      facebook: 'bg-blue-100 text-blue-700 border-blue-300',
      instagram: 'bg-pink-100 text-pink-700 border-pink-300',
      linkedin: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      google: 'bg-red-100 text-red-700 border-red-300',
      display: 'bg-purple-100 text-purple-700 border-purple-300',
      all: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return colors[platform.toLowerCase()] || colors.all;
  };

  const getAudienceStats = (audience: Audience): string => {
    const stats: string[] = [];

    // Age range
    if (audience.age_min || audience.age_max) {
      const min = audience.age_min || '?';
      const max = audience.age_max || '?';
      stats.push(`👥 Age ${min}-${max}`);
    }

    // Locations count
    if (audience.locations) {
      try {
        const locations = JSON.parse(audience.locations);
        if (locations.length > 0) {
          stats.push(`📍 ${locations.length} location${locations.length !== 1 ? 's' : ''}`);
        }
      } catch (e) {}
    }

    // Job titles count (LinkedIn)
    if (audience.job_titles) {
      try {
        const titles = JSON.parse(audience.job_titles);
        if (titles.length > 0) {
          stats.push(`💼 ${titles.length} job title${titles.length !== 1 ? 's' : ''}`);
        }
      } catch (e) {}
    }

    // Interests count (Facebook)
    if (audience.interests) {
      try {
        const interests = JSON.parse(audience.interests);
        if (interests.length > 0) {
          stats.push(`❤️ ${interests.length} interest${interests.length !== 1 ? 's' : ''}`);
        }
      } catch (e) {}
    }

    // Industries count (LinkedIn)
    if (audience.industries) {
      try {
        const industries = JSON.parse(audience.industries);
        if (industries.length > 0) {
          stats.push(`🏢 ${industries.length} industr${industries.length !== 1 ? 'y' : 'ies'}`);
        }
      } catch (e) {}
    }

    return stats.join(' • ') || 'No targeting defined';
  };

  const filteredAudiences = audiences.filter(audience =>
    audience.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (audience.description && audience.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading audiences...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search audiences..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => onOpenAudienceModal()}
          className="ml-4 flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Audience
        </button>
      </div>

      {/* Audiences grid */}
      {filteredAudiences.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery ? 'No audiences found' : 'No audiences yet'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchQuery
              ? 'Try adjusting your search'
              : 'Create your first audience to reuse targeting across campaigns.'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => onOpenAudienceModal()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Audience
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Audience Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Targeting</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAudiences.map((audience) => (
                <tr key={audience.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 text-sm">{audience.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded border ${getPlatformColor(
                        audience.platform
                      )}`}
                    >
                      {audience.platform.charAt(0).toUpperCase() + audience.platform.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600 max-w-xs truncate">
                      {audience.description || '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-gray-500">
                      {getAudienceStats(audience)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {deleteConfirm === audience.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleDelete(audience.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-gray-600 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onOpenAudienceModal(audience)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDuplicate(audience)}
                          className="text-gray-600 hover:text-gray-700"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(audience.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}